import { Link } from 'react-router';

interface ProductCardProps {
  slug: string;
  name: string;
  description: string;
  sizes: string[];
  price: string | null;
  status: string;
  mainPhoto: { url: string; altText: string | null } | null;
}

const statusColors: Record<string, string> = {
  available: 'bg-[var(--color-status-available)] text-white',
  reserved: 'bg-[var(--color-status-reserved)] text-white',
  sold: 'bg-[var(--color-status-sold)] text-white',
  preorder_only: 'bg-[var(--color-status-preorder)] text-white',
};

const statusLabels: Record<string, string> = {
  available: 'Available',
  reserved: 'Reserved',
  sold: 'Sold',
  preorder_only: 'Preorder Only',
};

export function ProductCard({
  slug,
  name,
  description,
  sizes,
  price,
  status,
  mainPhoto,
}: ProductCardProps) {
  const isSold = status === 'sold' || status === 'reserved';

  return (
    <Link
      to={`/inventory/${slug}`}
      className={`group block bg-[var(--color-brand-surface)] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
        isSold ? 'opacity-75' : ''
      }`}
    >
      <div className="aspect-square bg-[var(--color-glam-cream)] relative overflow-hidden">
        {mainPhoto ? (
          <img
            src={mainPhoto.url}
            alt={mainPhoto.altText ?? name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--color-brand-muted)]">
            <span className="text-4xl">✨</span>
          </div>
        )}
        <span
          className={`absolute top-3 right-3 text-xs font-medium px-2 py-1 rounded-full ${statusColors[status] ?? ''}`}
        >
          {statusLabels[status] ?? status}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-lg group-hover:text-[var(--color-brand-primary)] transition-colors">
          {name}
        </h3>
        <p className="text-sm text-[var(--color-brand-muted)] mt-1 line-clamp-2">
          {description}
        </p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-[var(--color-brand-muted)]">
            Size{sizes.length > 1 ? 's' : ''}: {sizes.join(', ')}
          </span>
          {price && (
            <span className="font-bold text-[var(--color-brand-primary)]">
              ${price}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
